import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  State,
  StateContext,
} from '@ngxs/store';
import { append, patch } from '@ngxs/store/operators';
import { catchError, EMPTY, tap } from 'rxjs';
import { nameof } from 'ts-simple-nameof';
import { RandomColorGenerator } from '../../../core/base';
import { DAWA_MAX_LENGTH } from '../../../core/domain/constants/application-constants';
import {
  Address,
  Dawa,
  DownloadSampleFile,
  LoaderHandler,
  Location,
  LocationType,
  Product,
  ProductActivity,
  PublicLocation,
  SegmentSuggestions,
} from '../../../core/domain/models';
import { AppliedFilters } from '../../../core/domain/models/applied-filter.model';
import { CountryEntity } from '../../../core/domain/models/country.model';
import { GetPrivateLocationsResponse } from '../../../core/domain/models/get-private-location.model';
import { PrivateLocationQuery } from '../../../core/domain/models/private-Location-request.model';
import {
  AddPublicLocationUsecase,
  CreateEditPublicLocationUsecase,
  DeactivatePublicLocationUsecase,
  DeletePublicLocationUsecase,
  GetActivitiesByProductUsecase,
  GetCountriesUsecase,
  GetDawaAutoCompleteLocationsUsecase,
  GetLocationsImportSampleFileUsecase,
  GetLocationTypeUsecase,
  GetPrivateLocationsUsecase,
  GetPrivateLocationUsecase,
  GetProductsUsecase,
  GetPublicLocationsUsecase,
  ImportLocationsFromFileUsecase,
  GetGLSLocationsUsecase
} from '../../../core/usecases';
import { GetSegmentSuggestionsUsecase } from '../../../core/usecases/location/get-segment-suggestions.usecase';
import { GetSortingOptionLookupUsecase } from '../../../core/usecases/location/get-sorting-optins-lookup.usecase';
import {
  DisableLoading,
  EnableLoading,
  SetIsCreated,
  SetIsDeleted,
  SetIsUpdated,
  SetLocationId,
} from '../../base/state/base.actions';
import { BaseState } from '../../base/state/base.state';
import {
  AddEditPrivateLocationState,
  AddEditPrivateLocationStateModel,
} from '../add-edit-private-location/state/add-edit-private-location.state';
import {
  AddPublicLocation,
  CreateLocation,
  DeactivateLocation,
  DeleteLocation,
  EditLocation,
  GetCountries,
  GetDawaAutoCompleteLocations,
  GetGLSLocations,
  GetLocationsImportSampleFile,
  GetLocationTypes,
  GetPrivateLocationById,
  GetPrivateLocations,
  GetProductActivities,
  GetProducts,
  GetSegmentSuggestions,
  GetSortingOptions,
  ImportLocationsFromFile,
  IsLocationDetailShow,
  IsLocationInfoSaved,
  IsProductShown,
  IsProductsSave,
  IsSegmentUpdateForProducts,
  IsSidebarToggled,
  IsSubLocationSaved,
  PublicLocations,
  ReSetLocationInfo,
  ReSetProducts,
  ReSetSubLocations,
  SetAppliedSortings,
  SetFilterRequest,
  SetIsAddressLocationLoading,
  SetPrivateLocations,
  SetProducts,
  SetSelectedLocation,
} from './locations.actions';
import { ResetAllStates } from '../../state/master-locations-global.actions';
import { Gls, GlsAddress } from '../../../core/domain/models/gls.model';

export class LocationsStateModel {
  public locationId: string | undefined;
  public mLSlocationId: string | undefined;
  public selectedDawaLocation: PublicLocation | undefined;
  public DawaAutoCompleteLocations: Dawa[] | undefined;
  public GLSLocations: Gls[] | undefined;
  public publicLocations: PublicLocation[] | undefined;
  public privateLocations: Location[] | undefined;
  public selectedLocation: Location | undefined;
  public products: Product[] | undefined;
  public productActivities: ProductActivity[] | undefined;
  public locationTypes: LocationType[] | undefined;
  public country: CountryEntity[] | undefined;
  public isProductShown: boolean | undefined;
  public isLocationDetailsShow: boolean | undefined;
  public importSampleFile: DownloadSampleFile | undefined;
  public privateLocationsQuery: PrivateLocationQuery | undefined;
  public isSegmentUpdateForProducts: boolean | undefined;
  public segmentSuggestions: SegmentSuggestions[] | undefined;
  public totalLocations: number | undefined;
  public isLocationInfoSaved: boolean | undefined;
  public isProductsSaved: boolean | undefined;
  public isSubLocationSaved: boolean | undefined;
  public reSetSubLocations: boolean | undefined;
  public reSetProducts: boolean | undefined;
  public reSetLocationInfo: boolean | undefined;
  public sortingOPtions: LocationType[] | undefined;
  public appliedFilters: AppliedFilters[] | undefined;
  public filters: any[] | undefined;
  public filterRequest: any | undefined;
  public appliedSorting: any | undefined;
  public isAddressLocationLoading: boolean | undefined;
  IsSidebarToggled: boolean | undefined;
}

const defaults = {
  selectedDawaLocation: undefined,
  DawaAutoCompleteLocations: undefined,
  GLSLocations: undefined,
  publicLocations: undefined,
  privateLocations: [],
  selectedLocation: undefined,
  products: undefined,
  productActivities: undefined,
  locationTypes: undefined,
  country: undefined,
  isProductShown: undefined,
  isLocationDetailsShow: false,
  importSampleFile: undefined,
  privateLocationsQuery: undefined,
  isSegmentUpdateForProducts: false,
  segmentSuggestions: undefined,
  locationId: undefined,
  mLSlocationId: undefined,
  totalLocations: undefined,
  isSubLocationSaved: false,
  isProductsSaved: false,
  isLocationInfoSaved: false,
  reSetLocationInfo: false,
  reSetProducts: false,
  reSetSubLocations: false,
  sortingOPtions: undefined,
  appliedFilters: [],
  filters: [],
  filterRequest: undefined,
  appliedSorting: undefined,
  isAddressLocationLoading: undefined,
  IsSidebarToggled: true,
};

@State<LocationsStateModel>({
  name: 'mls_locations',
  defaults,
  children: [AddEditPrivateLocationState],
})
@Injectable()
export class LocationsState extends BaseState {
  constructor(
    private addPublicLocationUsecase: AddPublicLocationUsecase,
    private getDawaAutoCompleteLocationsUsecase: GetDawaAutoCompleteLocationsUsecase,
    private getGLSLocationsUsecase: GetGLSLocationsUsecase,
    private getPrivateLocationUsecase: GetPrivateLocationUsecase,
    private getPrivateLocationsUsecase: GetPrivateLocationsUsecase,
    private getPublicLocationsUsecase: GetPublicLocationsUsecase,
    private getProductsUsecase: GetProductsUsecase,
    private getProductActivitiesUsecase: GetActivitiesByProductUsecase,
    private deletePublicLocationUsecase: DeletePublicLocationUsecase,
    private deactivatePublicLocationUsecase: DeactivatePublicLocationUsecase,
    private createEditPublicLocationUsecase: CreateEditPublicLocationUsecase,
    private getLocationTypeUsecase: GetLocationTypeUsecase,
    private getCountriesUsecase: GetCountriesUsecase,
    private importLocationsFromFileUsecase: ImportLocationsFromFileUsecase,
    private getLocationsImportSamlpeFileUsecase: GetLocationsImportSampleFileUsecase,
    private getSegmentSuggestionUsecase: GetSegmentSuggestionsUsecase,
    private getSortingOptionUsecase: GetSortingOptionLookupUsecase
  ) {
    super();
  }

  @Selector()
  static SelectedDawaLocation(state: LocationsStateModel) {
    return state.selectedDawaLocation;
  }

  @Selector()
  static DawaAutoCompleteLocations(state: LocationsStateModel) {
    return state.DawaAutoCompleteLocations;
  }

  @Selector()
  static GLSLocations(state: LocationsStateModel) {
    return state.GLSLocations;
  }

  @Selector()
  static PublicLocations(state: LocationsStateModel) {
    return state.publicLocations;
  }

  @Selector()
  static PrivateLocations(state: LocationsStateModel) {
    return state.privateLocations;
  }

  @Selector()
  static SelectedLocation(state: LocationsStateModel) {
    return state.selectedLocation;
  }

  @Selector()
  static Products(state: LocationsStateModel) {
    return state.products;
  }

  @Selector()
  static ProductActivities(state: LocationsStateModel) {
    return state.productActivities;
  }

  @Selector()
  static LocationTypes(state: LocationsStateModel) {
    return state.locationTypes;
  }

  @Selector()
  static IsProductShown(state: LocationsStateModel) {
    return state.isProductShown;
  }

  @Selector()
  static getLocationImportFile(state: LocationsStateModel) {
    return state.importSampleFile;
  }

  @Selector()
  static IsLocationDetailShow(state: LocationsStateModel) {
    return state.isLocationDetailsShow;
  }

  @Selector()
  static SortingOptions(state: LocationsStateModel) {
    return state.sortingOPtions;
  }

  @Selector()
  static IsAddressLocationLoading(state: LocationsStateModel) {
    return state.isAddressLocationLoading;
  }

  @Selector()
  static GetLocationId(state: LocationsStateModel) {
    return state.locationId;
  }
  @Selector()
  static GetMLSLocationId(state: LocationsStateModel) {
    return state.mLSlocationId;
  }
  @Selector()
  static getLocationRequest(state: LocationsStateModel) {
    return state.privateLocationsQuery;
  }

  @Selector()
  static getIsSegmentUpdateForProducts(state: LocationsStateModel) {
    return state.isSegmentUpdateForProducts;
  }
  @Selector()
  static IsLocationInfoSaved(state: LocationsStateModel) {
    return state.isLocationInfoSaved;
  }
  @Selector()
  static IsSubLocationSaved(state: LocationsStateModel) {
    return state.isSubLocationSaved;
  }
  @Selector()
  static IsProductsSaved(state: LocationsStateModel) {
    return state.isProductsSaved;
  }
  @Selector()
  static getSegmentSuggestions(state: LocationsStateModel) {
    return state.segmentSuggestions;
  }

  @Selector()
  static TotalLocations(state: LocationsStateModel) {
    return state.totalLocations;
  }

  @Selector()
  static ReSetLocationInfo(state: LocationsStateModel) {
    return state.reSetLocationInfo;
  }
  @Selector()
  static ReSetProducts(state: LocationsStateModel) {
    return state.reSetProducts;
  }
  @Selector()
  static ReSetSubLocations(state: LocationsStateModel) {
    return state.reSetSubLocations;
  }

  @Selector()
  static AppliedFilters(state: LocationsStateModel) {
    return state.appliedFilters;
  }

  @Selector()
  static Filters(state: LocationsStateModel) {
    return state.filters;
  }

  @Selector()
  static FilterRequest(state: LocationsStateModel) {
    return state.filterRequest;
  }

  @Selector()
  static AppliedSortings(state: LocationsStateModel) {
    return state.appliedSorting;
  }

  @Selector([AddEditPrivateLocationState])
  static locationInfo(
    state: LocationsStateModel,
    subState: AddEditPrivateLocationStateModel
  ) {
    return subState.locationInfo;
  }

  @Action(ReSetSubLocations)
  ReSetSubLocations(
    ctx: StateContext<LocationsStateModel>,
    { payload }: ReSetSubLocations
  ) {
    ctx.setState({ ...ctx.getState(), reSetSubLocations: payload });
  }

  @Action(ReSetProducts)
  ReSetProducts(
    ctx: StateContext<LocationsStateModel>,
    { payload }: ReSetProducts
  ) {
    ctx.setState({ ...ctx.getState(), reSetProducts: payload });
  }
  @Action(ReSetLocationInfo)
  ReSetLocationInfo(
    ctx: StateContext<LocationsStateModel>,
    { payload }: ReSetLocationInfo
  ) {
    ctx.setState({ ...ctx.getState(), reSetLocationInfo: payload });
  }

  @Action(SetIsAddressLocationLoading)
  SetIsAddressLocationLoading(
    ctx: StateContext<LocationsStateModel>,
    { payload }: SetIsAddressLocationLoading
  ) {
    ctx.setState({ ...ctx.getState(), isAddressLocationLoading: payload });
  }

  @Action(PublicLocations)
  getPublicLocation(
    ctx: StateContext<LocationsStateModel>,
    { payload }: PublicLocations
  ) {
    if (!payload) {
      return EMPTY;
    }
    return this.getPublicLocationsUsecase.execute(payload).pipe(
      tap((response) => {
        ctx.setState({ ...ctx.getState(), publicLocations: response });
      }),
      catchError((err: HttpErrorResponse) => {
        console.error(err);
        throw err;
      })
    );
  }

  @Action(GetPrivateLocations)
  getPrivateLocations(
    ctx: StateContext<LocationsStateModel>,
    { payload }: GetPrivateLocations
  ) {
    if (!payload) {
      return EMPTY;
    }
    const loaderHandler: LoaderHandler = {
      componentName: LocationsState.name,
      endPointName: nameof<LocationsState>(
        (state) => state.getPrivateLocations
      ),
    };
    ctx.setState({ ...ctx.getState(), locationId: undefined });
    ctx.setState({ ...ctx.getState(), mLSlocationId: undefined });
    ctx.dispatch(new EnableLoading(loaderHandler));
    return this.getPrivateLocationsUsecase.execute(payload).pipe(
      tap((response: GetPrivateLocationsResponse) => {
        ctx.dispatch(new DisableLoading(loaderHandler));
        if (payload.pageNumber == 1) {
          ctx.setState(
            patch<LocationsStateModel>({
              privateLocations: response.privateLocations,
              privateLocationsQuery: payload,
            })
          );
        } else {
          ctx.setState(
            patch<LocationsStateModel>({
              privateLocations: append(response.privateLocations),
              privateLocationsQuery: payload,
            })
          );
        }

        ctx.setState({
          ...ctx.getState(),
          totalLocations: response.totalLocations,
        });
      }),
      catchError((err: HttpErrorResponse) => {
        console.error(err);
        ctx.dispatch(new DisableLoading(loaderHandler));
        throw err;
      })
    );
  }

  @Action(SetPrivateLocations)
  setPrivateLocations(
    ctx: StateContext<LocationsStateModel>,
    { payload }: SetPrivateLocations
  ) {
    ctx.setState({ ...ctx.getState(), privateLocations: payload });
  }

  @Action(GetPrivateLocationById)
  getPrivateLocationById(
    ctx: StateContext<LocationsStateModel>,
    { payload }: GetPrivateLocationById
  ) {
    if (!payload) {
      return ctx.setState({ ...ctx.getState(), selectedLocation: undefined });
    }
    const loaderHandler: LoaderHandler = {
      componentName: LocationsState.name,
      endPointName: nameof<LocationsState>(
        (state) => state.getPrivateLocationById
      ),
    };
    ctx.dispatch(new EnableLoading(loaderHandler));
    return this.getPrivateLocationUsecase.execute(payload).pipe(
      tap((response: Location) => {
        ctx.setState({ ...ctx.getState(), selectedLocation: response });
        ctx.dispatch(new DisableLoading(loaderHandler));
      }),
      catchError((err: HttpErrorResponse) => {
        ctx.dispatch(new DisableLoading(loaderHandler));
        console.error(err);
        throw err;
      })
    );
  }

  @Action(GetDawaAutoCompleteLocations)
  getDawaAutoCompleteLocations(
    ctx: StateContext<LocationsStateModel>,
    { payload }: GetDawaAutoCompleteLocations
  ) {
    if (!payload) {
      return ctx.setState({
        ...ctx.getState(),
        DawaAutoCompleteLocations: undefined,
      });
    }
    const loaderHandler: LoaderHandler = {
      componentName: LocationsState.name,
      endPointName: nameof<LocationsState>(
        (state) => state.getDawaAutoCompleteLocations
      ),
    };
    ctx.dispatch(new SetIsAddressLocationLoading(true));
    ctx.dispatch(new EnableLoading(loaderHandler));

    payload =
      payload.length > DAWA_MAX_LENGTH
        ? payload.substring(0, DAWA_MAX_LENGTH)
        : payload;

    return this.getDawaAutoCompleteLocationsUsecase.execute(payload).pipe(
      tap((response: Dawa[]) => {
        ctx.dispatch(new SetIsAddressLocationLoading(false));
        if (response.length == 0) {
          response.push({
            text: 'Create location: ' + payload,
            address: new Address(),
          });
        }

        ctx.setState({
          ...ctx.getState(),
          DawaAutoCompleteLocations: response,
        });
        ctx.dispatch(new DisableLoading(loaderHandler));

      }),
      catchError((err: HttpErrorResponse) => {
        ctx.dispatch(new SetIsAddressLocationLoading(false));
        ctx.dispatch(new DisableLoading(loaderHandler));
        console.error(err);
        throw err;
      })
    );
  }

  @Action(GetGLSLocations)
  getGLSLocations(
    ctx: StateContext<LocationsStateModel>,
    { payload }: GetGLSLocations
  ) {
    if (!payload) {
      return ctx.setState({
        ...ctx.getState(),
        GLSLocations: undefined,
      });
    }
    const loaderHandler: LoaderHandler = {
      componentName: LocationsState.name,
      endPointName: nameof<LocationsState>(
        (state) => state.getGLSLocations
      ),
    };
    ctx.dispatch(new SetIsAddressLocationLoading(true));
    ctx.dispatch(new EnableLoading(loaderHandler));

    payload =
      payload.length > DAWA_MAX_LENGTH
        ? payload.substring(0, DAWA_MAX_LENGTH)
        : payload;

    return this.getGLSLocationsUsecase.execute(payload).pipe(
      tap((response: Gls[]) => {
        ctx.dispatch(new SetIsAddressLocationLoading(false));

        // Handle empty response - add "Create location" option
        if (!response || response.length === 0) {
          response = [{
            text: 'Create location: ' + payload,
            address: new GlsAddress(),
          }];
        }

        ctx.setState({
          ...ctx.getState(),
          GLSLocations: response,
        });
        ctx.dispatch(new DisableLoading(loaderHandler));
      }),
      catchError((err: HttpErrorResponse) => {
        ctx.dispatch(new SetIsAddressLocationLoading(false));
        ctx.dispatch(new DisableLoading(loaderHandler));
        console.error(err);
        throw err;
      })
    );
  }

  @Action(AddPublicLocation)
  addPublicLocation(
    ctx: StateContext<LocationsStateModel>,
    { payload }: AddPublicLocation
  ) {
    if (!payload) {
      return ctx.setState({
        ...ctx.getState(),
        selectedDawaLocation: undefined,
      });
    }
    const loaderHandler: LoaderHandler = {
      componentName: LocationsState.name,
      endPointName: nameof<LocationsState>((state) => state.addPublicLocation),
    };
    ctx.dispatch(new EnableLoading(loaderHandler));
    return this.addPublicLocationUsecase.execute(payload).pipe(
      tap((response: string) => {
        payload.id = response;
        const publicLocation: PublicLocation = {
          id: response,
          name: payload?.name,
          publicLocationId: null!,
          longitude: payload?.geoLocation.longitude,
          latitude: payload?.geoLocation.latitude,
          address: payload?.address,
          postalCode: payload?.postalCode,
          partialAddress: payload?.partialAddress ?? '',
          litra: payload?.litra ?? '',
          door: payload.door ?? '',
          floor: payload.floor ?? '',
          houseNo: payload.houseNo ?? '',
          city: payload?.city,
          country: payload?.country,
        };
        ctx.setState({
          ...ctx.getState(),
          selectedDawaLocation: publicLocation,
        });
        ctx.dispatch(new DisableLoading(loaderHandler));
      }),
      catchError((err: HttpErrorResponse) => {
        ctx.dispatch(new DisableLoading(loaderHandler));
        console.error(err);
        throw err;
      })
    );
  }

  @Action(SetSelectedLocation)
  setSelectedLocation(
    ctx: StateContext<LocationsStateModel>,
    { payload }: SetSelectedLocation
  ) {
    ctx.setState({ ...ctx.getState(), selectedLocation: payload });
  }

  @Action(GetProducts)
  getProducts(ctx: StateContext<LocationsStateModel>) {
    const loaderHandler: LoaderHandler = {
      componentName: LocationsState.name,
      endPointName: nameof<LocationsState>((state) => state.getProducts),
    };
    ctx.dispatch(new EnableLoading(loaderHandler));
    return this.getProductsUsecase.execute().pipe(
      tap((response) => {
        ctx.setState({ ...ctx.getState(), products: response });
        ctx.dispatch(new DisableLoading(loaderHandler));
      }),
      catchError((err: HttpErrorResponse) => {
        console.error(err);
        ctx.dispatch(new DisableLoading(loaderHandler));
        throw err;
      })
    );
  }

  @Action(SetProducts)
  setProducts(
    ctx: StateContext<LocationsStateModel>,
    { payload }: SetProducts
  ) {
    ctx.setState({ ...ctx.getState(), products: payload });
  }

  @Action(GetProductActivities)
  getProductActivities(
    ctx: StateContext<LocationsStateModel>,
    { payload }: GetProductActivities
  ) {
    if (payload) {
      const loaderHandler: LoaderHandler = {
        componentName: LocationsState.name,
        endPointName: nameof<LocationsState>(
          (state) => state.getProductActivities
        ),
      };
      ctx.dispatch(new EnableLoading(loaderHandler));
      return this.getProductActivitiesUsecase.execute(payload).pipe(
        tap((response) => {
          ctx.setState({ ...ctx.getState(), productActivities: response });
          ctx.dispatch(new DisableLoading(loaderHandler));
        }),
        catchError((err: HttpErrorResponse) => {
          console.error(err);
          ctx.dispatch(new DisableLoading(loaderHandler));
          throw err;
        })
      );
    } else {
      return ctx.setState({ ...ctx.getState(), productActivities: undefined });
    }
  }

  @Action(CreateLocation)
  createLocation(
    ctx: StateContext<LocationsStateModel>,
    { payload }: CreateLocation
  ) {
    const loaderHandler: LoaderHandler = {
      componentName: LocationsState.name,
      endPointName: nameof<LocationsState>((state) => state.createLocation),
    };
    ctx.dispatch(new EnableLoading(loaderHandler));
    return this.createEditPublicLocationUsecase.execute(payload).pipe(
      tap((response) => {
        if (response) {
          ctx.dispatch(new SetLocationId(undefined));
          ctx.dispatch(new SetLocationId(response.id));
          ctx.setState({
            ...ctx.getState(),
            mLSlocationId: response.locationId,
          });
          ctx.dispatch(new DisableLoading(loaderHandler));
          ctx.dispatch(new SetIsCreated(true));
        }
      }),
      catchError((err: HttpErrorResponse) => {
        console.error(err);
        ctx.dispatch(new DisableLoading(loaderHandler));
        throw err;
      })
    );
  }

  @Action(SetLocationId)
  SetLocationId(
    ctx: StateContext<LocationsStateModel>,
    { payload }: SetLocationId
  ) {
    ctx.setState({ ...ctx.getState(), locationId: payload });
  }

  @Action(EditLocation)
  editLocation(
    ctx: StateContext<LocationsStateModel>,
    { payload }: EditLocation
  ) {
    const loaderHandler: LoaderHandler = {
      componentName: LocationsState.name,
      endPointName: nameof<LocationsState>((state) => state.editLocation),
    };
    ctx.dispatch(new EnableLoading(loaderHandler));
    return this.createEditPublicLocationUsecase.execute(payload).pipe(
      tap((response) => {
        if (response) {
          ctx.dispatch(new SetLocationId(undefined));
          ctx.dispatch(new SetLocationId(response.id));
          ctx.setState({
            ...ctx.getState(),
            mLSlocationId: response.locationId,
          });

          ctx.dispatch(new SetIsUpdated(true));
          ctx.dispatch(new DisableLoading(loaderHandler));
        }
      }),
      catchError((err: HttpErrorResponse) => {
        console.error(err);
        ctx.dispatch(new DisableLoading(loaderHandler));
        throw err;
      })
    );
  }

  @Action(DeleteLocation)
  deleteLocation(
    ctx: StateContext<LocationsStateModel>,
    { payload }: DeleteLocation
  ) {
    const loaderHandler: LoaderHandler = {
      componentName: LocationsState.name,
      endPointName: nameof<LocationsState>((state) => state.deleteLocation),
    };
    ctx.dispatch(new EnableLoading(loaderHandler));
    return this.deletePublicLocationUsecase.execute(payload).pipe(
      tap((response) => {
        if (response) {
          ctx.dispatch(new SetIsDeleted(true));
          ctx.dispatch(
            new PublicLocations({
              searchParam: null,
              sortBy: null,
              sortOrder: null,
            })
          );
          ctx.dispatch(new DisableLoading(loaderHandler));
        }
      }),
      catchError((err: HttpErrorResponse) => {
        console.error(err);
        ctx.dispatch(new DisableLoading(loaderHandler));
        throw err;
      })
    );
  }

  @Action(DeactivateLocation)
  deactivateLocation(
    ctx: StateContext<LocationsStateModel>,
    { payload }: DeactivateLocation
  ) {
    const loaderHandler: LoaderHandler = {
      componentName: LocationsState.name,
      endPointName: nameof<LocationsState>((state) => state.deactivateLocation),
    };
    ctx.dispatch(new EnableLoading(loaderHandler));
    return this.deactivatePublicLocationUsecase.execute(payload).pipe(
      tap((response) => {
        if (response) {
          ctx.dispatch(new SetIsUpdated(true));
          ctx.dispatch(
            new PublicLocations({
              searchParam: null,
              sortBy: null,
              sortOrder: null,
            })
          );
          ctx.dispatch(new DisableLoading(loaderHandler));
        }
      }),
      catchError((err: HttpErrorResponse) => {
        console.error(err);
        ctx.dispatch(new DisableLoading(loaderHandler));
        throw err;
      })
    );
  }

  @Action(GetLocationTypes)
  getLocationTypes(ctx: StateContext<LocationsStateModel>) {
    const loaderHandler: LoaderHandler = {
      componentName: LocationsState.name,
      endPointName: nameof<LocationsState>((state) => state.getLocationTypes),
    };
    ctx.dispatch(new EnableLoading(loaderHandler));
    return this.getLocationTypeUsecase.execute().pipe(
      tap((response) => {
        ctx.setState({ ...ctx.getState(), locationTypes: response });
        ctx.dispatch(new DisableLoading(loaderHandler));
      }),
      catchError((err: HttpErrorResponse) => {
        console.error(err);
        ctx.dispatch(new DisableLoading(loaderHandler));
        throw err;
      })
    );
  }

  @Action(GetCountries)
  getCountiesData(ctx: StateContext<LocationsStateModel>) {
    const loaderHandler: LoaderHandler = {
      componentName: LocationsState.name,
      endPointName: nameof<LocationsState>((state) => state.getCountiesData),
    };
    ctx.dispatch(new EnableLoading(loaderHandler));
    return this.getCountriesUsecase.execute().pipe(
      tap((response) => {
        ctx.setState({ ...ctx.getState(), country: response });
        ctx.dispatch(new DisableLoading(loaderHandler));
      }),
      catchError((err: HttpErrorResponse) => {
        console.error(err);
        ctx.dispatch(new DisableLoading(loaderHandler));
        throw err;
      })
    );
  }

  @Action(GetSortingOptions)
  getSortingOptions(ctx: StateContext<LocationsStateModel>) {
    const loaderHandler: LoaderHandler = {
      componentName: LocationsState.name,
      endPointName: nameof<LocationsState>((state) => state.getSortingOptions),
    };
    ctx.dispatch(new EnableLoading(loaderHandler));
    return this.getSortingOptionUsecase.execute().pipe(
      tap((response) => {
        ctx.setState({ ...ctx.getState(), sortingOPtions: response });
        ctx.dispatch(new DisableLoading(loaderHandler));
      }),
      catchError((err: HttpErrorResponse) => {
        console.error(err);
        ctx.dispatch(new DisableLoading(loaderHandler));
        throw err;
      })
    );
  }

  @Action(IsProductShown)
  ISProductShown(
    ctx: StateContext<LocationsStateModel>,
    { payload }: IsProductShown
  ) {
    return ctx.setState({ ...ctx.getState(), isProductShown: payload });
  }

  @Action(IsLocationDetailShow)
  IsLocationDetailShow(
    ctx: StateContext<LocationsStateModel>,
    { payload }: IsLocationDetailShow
  ) {
    ctx.setState({ ...ctx.getState(), isLocationDetailsShow: payload });
  }

  // @Action(SetAppliedFilters)
  // SetAppliedFilters(
  //   ctx: StateContext<LocationsStateModel>,
  //   { payload }: { payload: AppliedFilters[] }
  // ) {
  //   return ctx.setState({ ...ctx.getState(), appliedFilters: payload });
  // }

  // @Action(SetFilters)
  // SetFilters(
  //   ctx: StateContext<LocationsStateModel>,
  //   { payload }: { payload: any[] }
  // ) {
  //   return ctx.setState({ ...ctx.getState(), filters: payload });
  // }

  @Action(SetAppliedSortings)
  SetSortings(
    ctx: StateContext<LocationsStateModel>,
    { payload }: { payload: any }
  ) {
    return ctx.setState({ ...ctx.getState(), appliedSorting: payload });
  }

  @Action(SetFilterRequest)
  SetFilterRequest(
    ctx: StateContext<LocationsStateModel>,
    { payload }: { payload: any }
  ) {
    return ctx.setState({ ...ctx.getState(), filterRequest: payload });
  }

  @Action(IsSegmentUpdateForProducts)
  IsSegmentUpdateForProducts(
    ctx: StateContext<LocationsStateModel>,
    { payload }: IsSegmentUpdateForProducts
  ) {
    ctx.setState({ ...ctx.getState(), isSegmentUpdateForProducts: payload });
  }
  @Action(IsLocationInfoSaved)
  IsLocationInfoSaved(
    ctx: StateContext<LocationsStateModel>,
    { payload }: IsLocationInfoSaved
  ) {
    ctx.setState({ ...ctx.getState(), isLocationInfoSaved: payload });
  }

  @Action(IsSubLocationSaved)
  IsSubLocationSaved(
    ctx: StateContext<LocationsStateModel>,
    { payload }: IsSubLocationSaved
  ) {
    ctx.setState({ ...ctx.getState(), isSubLocationSaved: payload });
  }
  @Action(IsProductsSave)
  IsrProductsSave(
    ctx: StateContext<LocationsStateModel>,
    { payload }: IsProductsSave
  ) {
    ctx.setState({ ...ctx.getState(), isProductsSaved: payload });
  }
  @Action(GetSegmentSuggestions)
  getSegmentSuggestions(
    ctx: StateContext<LocationsStateModel>,
    { payload }: GetSegmentSuggestions
  ) {
    const loaderHandler: LoaderHandler = {
      componentName: LocationsState.name,
      endPointName: nameof<LocationsState>(
        (state) => state.getSegmentSuggestions
      ),
    };
    ctx.dispatch(new EnableLoading(loaderHandler));
    return this.getSegmentSuggestionUsecase.execute(payload).pipe(
      tap((response) => {
        response.forEach(
          (x) => (x.color = RandomColorGenerator.getRandomColor())
        );
        ctx.setState({ ...ctx.getState(), segmentSuggestions: response });
        ctx.dispatch(new DisableLoading(loaderHandler));
      }),
      catchError((err: HttpErrorResponse) => {
        console.error(err);
        ctx.dispatch(new DisableLoading(loaderHandler));
        throw err;
      })
    );
  }

  @Action(ImportLocationsFromFile)
  importLocationsFromFile(
    ctx: StateContext<LocationsStateModel>,
    { payload }: ImportLocationsFromFile
  ) {
    const loaderHandler: LoaderHandler = {
      componentName: LocationsState.name,
      endPointName: nameof<LocationsState>(
        (state) => state.importLocationsFromFile
      ),
    };
    ctx.dispatch(new EnableLoading(loaderHandler));
    return this.importLocationsFromFileUsecase.execute(payload).pipe(
      tap((response) => {
        if (response) {
          ctx.dispatch(new SetIsCreated(true));
          ctx.dispatch(new DisableLoading(loaderHandler));
        }
      }),
      catchError((err: HttpErrorResponse) => {
        console.error(err);
        ctx.dispatch(new DisableLoading(loaderHandler));
        throw err;
      })
    );
  }

  @Action(GetLocationsImportSampleFile)
  getLocationImportSampleFile(
    ctx: StateContext<LocationsStateModel>,
    { payload }: GetLocationsImportSampleFile
  ) {
    const loaderHandler: LoaderHandler = {
      componentName: LocationsState.name,
      endPointName: nameof<LocationsState>(
        (state) => state.getLocationImportSampleFile
      ),
    };
    ctx.dispatch(new EnableLoading(loaderHandler));
    if (payload) {
      return this.getLocationsImportSamlpeFileUsecase.execute().pipe(
        tap((response) => {
          if (response) {
            ctx.setState({
              ...ctx.getState(),
              importSampleFile: {
                fileBlob: response,
                fileName: 'Locations_Import_Sample_File',
              },
            });
          }
          ctx.dispatch(new DisableLoading(loaderHandler));
        }),
        catchError((err: HttpErrorResponse) => {
          console.error(err);
          ctx.dispatch(new DisableLoading(loaderHandler));
          throw err;
        })
      );
    } else {
      ctx.dispatch(new DisableLoading(loaderHandler));
      return ctx.setState({ ...ctx.getState(), importSampleFile: undefined });
    }
  }
  @Action(ResetAllStates)
  resetAll(ctx: StateContext<LocationsStateModel>) {
    ctx.setState({ ...defaults });
  }

  @Action(IsSidebarToggled)
  IsSidebarToggled(
    ctx: StateContext<LocationsStateModel>,
    { payload }: IsSidebarToggled
  ) {
    ctx.setState({ ...ctx.getState(), IsSidebarToggled: payload });
  }

  @Selector()
  static IsSidebarToggled(state: LocationsStateModel) {
    return state?.IsSidebarToggled ?? true;
  }
}

export const selectLocationInfo = createSelector(
  [AddEditPrivateLocationState],
  (subState: AddEditPrivateLocationStateModel) => subState.locationInfo
);

export const products = createSelector(
  [AddEditPrivateLocationState],
  (subState: AddEditPrivateLocationStateModel) => subState.products
);

export const additionalDataKeys = createSelector(
  [AddEditPrivateLocationState],
  (subState: AddEditPrivateLocationStateModel) => subState.additionalDataKeys
);

export const additionalDataKey = createSelector(
  [AddEditPrivateLocationState],
  (subState: AddEditPrivateLocationStateModel) => subState.additionalDataKey
);
